/**
 * RichContent — renders trusted-but-sanitized HTML produced by RichTextEditor.
 * We whitelist only the tags/attrs used by the editor to avoid XSS.
 *
 * Bonus: `<a>` tags pointing to an image get a hover-thumbnail popover
 * so readers can preview "📷 Poza 1" without leaving the page.
 *
 * If `onOpenMaterialList` is provided, `<a href="#list:ID">` links open
 * the materials list modal instead of navigating.
 * If `onOpenProject` is provided, `<a href="#project:ID">` links call
 * that callback (typically routes to /doserne/prosjekter/ID).
 */
import React, { useEffect, useMemo, useRef } from "react";
import DOMPurify from "dompurify";
import { usePhotoHoverPreview } from "./usePhotoHoverPreview";

const ALLOWED_TAGS = [
  "p", "br", "strong", "em", "u", "s", "mark",
  "h1", "h2",
  "ul", "ol", "li",
  "a", "span",
];
const ALLOWED_ATTR = ["href", "target", "rel", "style", "class", "data-mlist", "data-project"];

export default function RichContent({ html = "", className = "", onOpenMaterialList = null, onOpenProject = null, projectAddresses = null }) {
  const ref = useRef(null);
  usePhotoHoverPreview(ref);

  const clean = useMemo(() => {
    if (!html) return "";
    return DOMPurify.sanitize(html, {
      ALLOWED_TAGS,
      ALLOWED_ATTR,
      ADD_ATTR: ["target", "rel", "data-mlist", "data-project"],
    });
  }, [html]);

  // Prepend a "📍 open in Maps" pin before every project pill that resolves
  // to a known project address. The pill carries the project id, so the pin
  // opens Google Maps at the real project address (no manual typing).
  useEffect(() => {
    if (!ref.current || !projectAddresses) return;
    const el = ref.current;
    el.querySelectorAll("a[href^='#project:']").forEach((a) => {
      const id = (a.getAttribute("href") || "").slice(9);
      const addr = projectAddresses?.[id]?.address;
      if (!addr) return;
      if (a.previousElementSibling && a.previousElementSibling.classList?.contains("plan-maps-pin")) return;
      const pin = document.createElement("a");
      pin.className = "plan-maps-pin";
      pin.href = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addr)}`;
      pin.target = "_blank";
      pin.rel = "noopener noreferrer";
      pin.title = addr;
      pin.textContent = "📍";
      pin.setAttribute("data-testid", `rc-project-maps-${id}`);
      a.parentNode.insertBefore(pin, a);
      a.parentNode.insertBefore(document.createTextNode(" "), a);
    });
  }, [clean, projectAddresses]);

  // Intercept #list:ID and #project:ID clicks
  useEffect(() => {
    if (!ref.current) return;
    if (!onOpenMaterialList && !onOpenProject) return;
    const el = ref.current;
    const handler = (e) => {
      const a = e.target.closest("a");
      if (!a) return;
      const href = a.getAttribute("href") || "";
      if (href.startsWith("#list:") && onOpenMaterialList) {
        e.preventDefault(); e.stopPropagation();
        onOpenMaterialList(href.slice(6), a);
        return;
      }
      if (href.startsWith("#project:") && onOpenProject) {
        e.preventDefault(); e.stopPropagation();
        onOpenProject(href.slice(9), a);
      }
    };
    el.addEventListener("click", handler);
    return () => el.removeEventListener("click", handler);
  }, [clean, onOpenMaterialList, onOpenProject]);

  if (!clean) return null;
  return (
    <div
      ref={ref}
      className={
        "max-w-none text-slate-800 " +
        "[&_h1]:text-2xl [&_h1]:font-black [&_h1]:mt-2 [&_h1]:mb-1 [&_h1]:text-slate-900 " +
        "[&_h2]:text-lg [&_h2]:font-bold [&_h2]:mt-2 [&_h2]:mb-1 [&_h2]:text-slate-900 " +
        "[&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 " +
        "[&_mark]:bg-yellow-200 [&_mark]:rounded [&_mark]:px-0.5 " +
        "[&_a]:underline [&_a]:text-emerald-700 [&_p]:my-1 " +
        "[&_a[href^='#list:']]:no-underline [&_a[href^='#list:']]:bg-amber-100 [&_a[href^='#list:']]:text-amber-900 " +
        "[&_a[href^='#list:']]:px-2 [&_a[href^='#list:']]:py-0.5 [&_a[href^='#list:']]:rounded-md " +
        "[&_a[href^='#list:']]:border [&_a[href^='#list:']]:border-amber-300 [&_a[href^='#list:']]:font-semibold " +
        "[&_a[href^='#list:']]:cursor-pointer [&_a[href^='#list:']:hover]:bg-amber-200 " +
        "[&_a[href^='#project:']]:no-underline [&_a[href^='#project:']]:bg-sky-100 [&_a[href^='#project:']]:text-sky-900 " +
        "[&_a[href^='#project:']]:px-2.5 [&_a[href^='#project:']]:py-1 [&_a[href^='#project:']]:rounded-lg " +
        "[&_a[href^='#project:']]:border [&_a[href^='#project:']]:border-sky-300 [&_a[href^='#project:']]:font-black " +
        "[&_a[href^='#project:']]:cursor-pointer [&_a[href^='#project:']:hover]:bg-sky-200 [&_a[href^='#project:']]:inline-flex " +
        "[&_.plan-maps-pin]:no-underline [&_.plan-maps-pin]:bg-emerald-100 [&_.plan-maps-pin]:border [&_.plan-maps-pin]:border-emerald-300 " +
        "[&_.plan-maps-pin]:rounded-md [&_.plan-maps-pin]:px-1.5 [&_.plan-maps-pin]:py-0.5 [&_.plan-maps-pin]:cursor-pointer [&_.plan-maps-pin:hover]:bg-emerald-200 " +
        "[&_strong]:font-bold [&_em]:italic [&_u]:underline [&_s]:line-through " +
        className
      }
      dangerouslySetInnerHTML={{ __html: clean }}
    />
  );
}
